import React from 'react';
import { Link, useLocation } from 'react-router-dom';

/**
 * Resolve a section anchor to an href that works from wherever it is rendered.
 *
 * The revise chrome is not landing-page-only: MarketingLayout also wraps
 * `/courses`, `/course/:id`, `/bundle/:id`, `/privacy` and `/terms`. A bare
 * `#study` on any of those points at a section that isn't in the document, so
 * the link silently does nothing. Off the landing page the anchor has to be
 * root-qualified.
 */
export const useAnchorHref = () => {
  const { pathname } = useLocation();
  const onLanding = pathname === '/';
  return (anchor: string) => (onLanding ? `#${anchor}` : `/#${anchor}`);
};

/**
 * A CTA that might be an in-page anchor or a route.
 *
 * v4 points the hero and closing CTAs at `#try` — the demo, not the sign-up —
 * and a react-router `<Link to="#try">` would treat that as a PATH and navigate
 * to `/#try` as a route rather than scrolling. Anchors therefore render as a
 * plain `<a>`, which also gets the browser to do the scrolling: react-router
 * does not scroll to a hash on navigation, so the client-side version would
 * land at the top of the page with the fragment in the URL and no movement.
 */
export const CtaLink: React.FC<{
  href: string;
  className?: string;
  children: React.ReactNode;
}> = ({ href, className, children }) => {
  const anchorHref = useAnchorHref();
  if (href.startsWith('#')) {
    return (
      <a className={className} href={anchorHref(href.slice(1))}>
        {children}
      </a>
    );
  }
  // Absolute URLs leave the app; anything else is an in-app route.
  if (/^https?:\/\//.test(href)) {
    return (
      <a className={className} href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  return (
    <Link className={className} to={href}>
      {children}
    </Link>
  );
};
